<script setup lang="ts">
import type { PreparedPhotoSwipeOptions } from 'photoswipe'
import PhotoSwipe from 'photoswipe/lightbox'
import 'photoswipe/style.css'

interface Props {
  name: string;
  options?: Partial<PreparedPhotoSwipeOptions>
}

const model = defineModel({ default: 0 })
const { name, options = {} } = defineProps<Props>()

const gallery = ref<HTMLElement | null>(null)

const lightbox = shallowRef(new PhotoSwipe({
  gallery: `#${name}`,
  children: '[data-lightbox]',
  ...options,
  pswpModule: () => import('photoswipe'),
}))

watch(model, () => {
  lightbox.value.pswp?.goTo(model.value)
})

lightbox.value.on('change', () => {
  model.value = lightbox.value.pswp?.currIndex || 0
})

lightbox.value.on('beforeOpen', () => {
  const bodyOverflow = document.body.style.overflow
  const bodyPaddingRight = document.body.style.paddingRight

  const scrollbarWidth = window.innerWidth - document.body.clientWidth

  document.body.style.overflowY = 'hidden'
  document.body.style.paddingRight = `${scrollbarWidth}px`

  lightbox.value.on('close', () => {
    document.body.style.overflowY = bodyOverflow
    document.body.style.paddingRight = bodyPaddingRight
  })
})

lightbox.value.on('uiRegister', function() {
  lightbox.value.pswp?.ui?.registerElement({
    name: 'caption',
    order: 9,
    isButton: false,
    appendTo: 'root',
    html: 'Caption text',
    onInit: (el, pswp) => {
      pswp.on('change', () => {
        el.textContent = pswp.currSlide?.data.element?.dataset.lightboxCaption || ''
      })
    }
  })
})


onMounted(() => {
  lightbox.value.init()
})

onUnmounted(() => {
  lightbox.value.destroy()
})

defineExpose({
  lightbox
})
</script>

<template>
  <div :id="name" ref="gallery">
    <slot />
  </div>
</template>

<style lang="scss">
.pswp {
  --pswp-bg: rgb(var(--secondary));

  &__bg {
    @apply backdrop-blur;
  }

  &__img {
    @apply cursor-pointer;
  }

  &__container {
    @apply transform-gpu will-change-transform transition-transform duration-[var(--pswp-transition-duration)] ease-out;
  }

  &__caption {
    @apply empty:hidden bg-secondary text-white max-w-full md:max-w-[600px] break-words px-[15px] py-[10px] rounded bottom-[20px] absolute left-1/2 -translate-x-1/2;
  }
}
</style>
